import * as MediaAssetAPI from '@/api/media-asset';
import MediaAssetCard from '@/components/media-asset/MediaAssetCard';
import MediaGrid from '@/components/media-asset/MediaGrid';
import { useCurrentApp } from '@/stores/userDataStore';
import MediaAssetsLoader from './MediaAssetsLoader';
import { StandaloneMediaAsset } from '@/types/media';
import {
  LibraryNotification,
  useLibraryNotificationsStore,
} from '@/features/notifications/stores/libraryNotificationsStore';
import { useCallback, useMemo, memo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Product } from '@/types/product';
import { Card, EmptyState } from '@shopify/polaris';

interface ImageAssetsProps {
  selectedProduct?: Product | null;
}

const filterMediaAssetsByProduct = (
  mediaAssets: StandaloneMediaAsset[],
  product?: Product | null
): StandaloneMediaAsset[] => {
  if (!product || !mediaAssets?.length) return mediaAssets || [];

  return mediaAssets.filter((asset) => {
    if (asset.productId) {
      return asset.productId === product.id || asset.productId === product.productId;
    }

    return false;
  });
};

export const ImageAssets = memo(({ selectedProduct }: ImageAssetsProps) => {
  const { appKey } = useCurrentApp();

  const { setNotifications } = useLibraryNotificationsStore(
    useShallow((state) => ({
      addNotification: state.addNotification,
      setNotifications: state.setNotifications,
    }))
  );

  const notifyAboutFailedItems = useCallback(
    (mediaAssets: StandaloneMediaAsset[]) => {
      const notifications: LibraryNotification[] = [];
      mediaAssets.forEach((mediaAsset) => {
        notifications.push({
          id: mediaAsset.id,
          source: 'media_asset',
          title: 'Failed to generate Media Asset',
          description: mediaAsset.name,
          tone: 'critical',
          toolUsed: mediaAsset.createdWith.toolName,
          generatedAt: mediaAsset.createdAt,
          error: mediaAsset.error,
        });
      });
      setNotifications(notifications);
    },
    [setNotifications]
  );

  const queryKey = useMemo(() => ['image-assets', appKey, selectedProduct?.id || 'all'], [appKey, selectedProduct?.id]);

  const renderItem = useCallback((item) => {
    return <MediaAssetCard key={item.id} media={item} inGrid />;
  }, []);

  const renderMediaAssets = useCallback(
    (mediaAssets: StandaloneMediaAsset[]) => {
      const filteredAssets = filterMediaAssetsByProduct(mediaAssets, selectedProduct);

      if (selectedProduct && filteredAssets.length === 0) {
        return (
          <Card>
            <EmptyState
              heading={`No images found for this product`}
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            />
          </Card>
        );
      }

      return <MediaGrid items={filteredAssets} renderItem={renderItem} staticHeight="calc(100vh - 270px)" />;
    },
    [renderItem, selectedProduct]
  );

  return (
    <MediaAssetsLoader
      queryKey={queryKey}
      loaderFn={MediaAssetAPI.getAiImages}
      notifyAboutFailedItems={notifyAboutFailedItems}
      renderMediaAssets={renderMediaAssets}
      size="large"
      staticHeight="calc(100vh - 270px)"
    />
  );
});

ImageAssets.displayName = 'ImageAssets';
